/**
 * Colour maths for making user-authored mail readable on whichever surface it
 * lands on. No DOM: everything here takes and returns plain numbers, so it can
 * be reasoned about and tested on its own.
 *
 * The problem this exists for: a mail written in Outlook carries the sender's
 * own colours, chosen against white paper. A signature set in `rgb(68, 68, 68)`
 * has a contrast ratio of 1.51 against our dark ticket surface and is
 * effectively invisible, while the same mail is perfectly legible in light mode.
 *
 * Naive lightness inversion — the usual reflex, and what Dark Reader does — is
 * wrong here, because it also moves colours that were fine: `#999999` goes from
 * a contrast of 5.15 on our dark surface to 2.56. Instead `repairColor` keeps
 * the sender's *intent*: it works out what contrast the colour achieved against
 * the paper it was written for, and reproduces that same contrast against the
 * surface it actually landed on, with WCAG AA as a floor. Hue and saturation
 * are held fixed, so blue stays blue and a grey signature stays visibly
 * subordinate to the body text.
 */

export type Rgb = [number, number, number]
export type Rgba = [number, number, number, number]

/** Leave anything at or above this alone: it is already legible. */
export const REPAIR_BELOW = 3

/** WCAG 2.1 AA for body text. Never aim lower, even when the sender did. */
export const CONTRAST_FLOOR = 4.5

/**
 * Resolution of the lightness search. 0.005 lands within a single 8-bit step
 * over most of the range, which is finer than anyone can see.
 */
const LIGHTNESS_STEP = 0.005

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

const HEX_PATTERN = /^#([0-9a-f]{3,8})$/i
const FUNCTIONAL_PATTERN = /^rgba?\(([^)]+)\)$/i

const OKLCH_PATTERN = /^okl(ch|ab)\(([^)]+)\)$/i

/**
 * OKLCh (and OKLab) to sRGB, after Björn Ottosson's reference conversion.
 *
 * Needed because Tailwind v4's palette is oklch, so this is the syntax
 * `getComputedStyle` hands back for every colour our own stylesheet sets —
 * the panel a mail is sitting on included. Without it every measurement would
 * fall back to a guessed surface.
 */
function oklabToRgb(L: number, a: number, b: number): Rgb {
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3)
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3)
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3)

  const linear = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]

  return linear.map(function (channel) {
    const gamma =
      channel <= 0.0031308
        ? 12.92 * channel
        : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055

    return clamp(Math.round(gamma * 255), 0, 255)
  }) as Rgb
}

/** One component of an `oklch()`/`oklab()` argument list. `none` counts as 0. */
function oklchComponent(part: string, percentageOf: number): number {
  if (part === 'none') {
    return 0
  }

  const value = parseFloat(part)

  return part.endsWith('%') ? (value / 100) * percentageOf : value
}

/**
 * Parses the colour syntaxes that actually reach us.
 *
 * `getComputedStyle` always hands back `rgb()` or `rgba()` whatever the source
 * wrote, so that is the case that matters at runtime. Hex is supported as well
 * because it is how the colours read in the tests and in a mail's own source.
 *
 * Returns `null` for anything unrecognised — `transparent` resolves to
 * `rgba(0, 0, 0, 0)` and so parses fine, but named colours and `color(...)`
 * spaces do not, and a caller that cannot measure a colour must leave it alone
 * rather than guess.
 */
export function parseCssColor(value: string | null | undefined): Rgba | null {
  if (!value) {
    return null
  }

  const input = value.trim()

  const hex = HEX_PATTERN.exec(input)
  if (hex) {
    const digits = hex[1]

    /** #rgb and #rgba are shorthand for doubled digits. */
    const expand =
      digits.length <= 4
        ? digits
            .split('')
            .map((digit) => digit + digit)
            .join('')
        : digits

    if (expand.length !== 6 && expand.length !== 8) {
      return null
    }

    const channel = (index: number): number =>
      parseInt(expand.substr(index * 2, 2), 16)

    return [
      channel(0),
      channel(1),
      channel(2),
      expand.length === 8 ? channel(3) / 255 : 1,
    ]
  }

  const oklch = OKLCH_PATTERN.exec(input)
  if (oklch) {
    const parts = oklch[2]
      .replace(/\//g, ' ')
      .split(/[\s,]+/)
      .filter((part) => part.length > 0)

    if (parts.length < 3) {
      return null
    }

    const lightness = oklchComponent(parts[0], 1)
    const alpha =
      parts.length > 3 ? clamp(oklchComponent(parts[3], 1), 0, 1) : 1

    /** `oklch()` carries chroma and hue; `oklab()` carries the axes directly. */
    const [a, b] =
      oklch[1].toLowerCase() === 'ch'
        ? (function () {
            const chroma = oklchComponent(parts[1], 0.4)
            const hue = (oklchComponent(parts[2], 1) * Math.PI) / 180

            return [chroma * Math.cos(hue), chroma * Math.sin(hue)]
          })()
        : [oklchComponent(parts[1], 0.4), oklchComponent(parts[2], 0.4)]

    if ([lightness, a, b, alpha].some(isNaN)) {
      return null
    }

    const [red, green, blue] = oklabToRgb(lightness, a, b)

    return [red, green, blue, alpha]
  }

  const functional = FUNCTIONAL_PATTERN.exec(input)
  if (!functional) {
    return null
  }

  /** Both the legacy comma form and the modern `rgb(r g b / a)` form. */
  const parts = functional[1]
    .replace(/\//g, ' ')
    .split(/[\s,]+/)
    .filter((part) => part.length > 0)

  if (parts.length < 3) {
    return null
  }

  const numbers = parts.slice(0, 4).map(function (part) {
    return part.endsWith('%') ? parseFloat(part) / 100 : parseFloat(part)
  })

  if (numbers.some(isNaN)) {
    return null
  }

  const alpha = numbers.length > 3 ? clamp(numbers[3], 0, 1) : 1

  return [
    clamp(Math.round(numbers[0]), 0, 255),
    clamp(Math.round(numbers[1]), 0, 255),
    clamp(Math.round(numbers[2]), 0, 255),
    alpha,
  ]
}

/** WCAG 2.1 relative luminance. */
export function relativeLuminance([r, g, b]: Rgb): number {
  const linear = (value: number): number => {
    const channel = value / 255

    return channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4)
  }

  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

/** WCAG 2.1 contrast ratio, between 1 and 21. Order of the arguments is irrelevant. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const first = relativeLuminance(a)
  const second = relativeLuminance(b)

  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

/**
 * Flattens a translucent colour onto an opaque one. Half-transparent text has
 * to be blended before it is measured, or it looks stronger than it renders.
 */
export function compositeOver(foreground: Rgba, background: Rgb): Rgb {
  const alpha = foreground[3]

  return [0, 1, 2].map(function (channel) {
    return Math.round(
      foreground[channel] * alpha + background[channel] * (1 - alpha),
    )
  }) as Rgb
}

export function rgbToHsl([r, g, b]: Rgb): [number, number, number] {
  const red = r / 255
  const green = g / 255
  const blue = b / 255

  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const lightness = (max + min) / 2

  if (max === min) {
    return [0, 0, lightness]
  }

  const delta = max - min
  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)

  let hue: number
  if (max === red) {
    hue = (green - blue) / delta + (green < blue ? 6 : 0)
  } else if (max === green) {
    hue = (blue - red) / delta + 2
  } else {
    hue = (red - green) / delta + 4
  }

  return [hue / 6, saturation, lightness]
}

export function hslToRgb([h, s, l]: [number, number, number]): Rgb {
  if (s === 0) {
    const grey = Math.round(l * 255)

    return [grey, grey, grey]
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q

  const component = (offset: number): number => {
    const t = (offset + 1) % 1

    if (t < 1 / 6) {
      return p + (q - p) * 6 * t
    }
    if (t < 1 / 2) {
      return q
    }
    if (t < 2 / 3) {
      return p + (q - p) * (2 / 3 - t) * 6
    }

    return p
  }

  return [
    Math.round(component(h + 1 / 3) * 255),
    Math.round(component(h) * 255),
    Math.round(component(h - 1 / 3) * 255),
  ]
}

/** Is this surface dark enough that text on it has to be light? */
export function isDarkSurface(surface: Rgb): boolean {
  return relativeLuminance(surface) < 0.5
}

/**
 * The paper a colour was most likely authored against.
 *
 * A colour that fails on our surface was chosen for a surface of the opposite
 * polarity — grey-on-white from a sender in light mode, white-on-grey from one
 * writing in Outlook's own dark mode. The dark value matches the app's own
 * panel so the two directions are symmetric.
 */
export function assumedPaper(surface: Rgb): Rgb {
  return isDarkSurface(surface) ? [255, 255, 255] : [31, 41, 55]
}

/**
 * Re-lights `foreground` so it is readable on `surface`, or returns `null` when
 * it already is and should be left exactly as the sender wrote it.
 *
 * The target is the contrast the colour had against the paper it was written
 * for, floored at AA — so black body text comes back as white rather than as
 * the mid-grey a bare AA target would give it, and a grey signature stays grey
 * rather than being promoted to the same weight as the body.
 *
 * Hue and saturation are preserved; only lightness moves, and only away from
 * the surface. When the target is out of reach — black text can never reach 21
 * against a dark panel — the extreme end of the range is taken.
 */
export function repairColor(
  foreground: Rgb,
  surface: Rgb,
  paper: Rgb = assumedPaper(surface),
): Rgb | null {
  if (contrastRatio(foreground, surface) >= REPAIR_BELOW) {
    return null
  }

  const target = Math.max(contrastRatio(foreground, paper), CONTRAST_FLOOR)
  const [hue, saturation] = rgbToHsl(foreground)
  const towardsLight = isDarkSurface(surface)

  let best: Rgb = hslToRgb([hue, saturation, towardsLight ? 1 : 0])

  for (let step = 0; step <= 1 + LIGHTNESS_STEP / 2; step += LIGHTNESS_STEP) {
    const lightness = clamp(towardsLight ? step : 1 - step, 0, 1)
    const candidate = hslToRgb([hue, saturation, lightness])

    if (contrastRatio(candidate, surface) >= target) {
      best = candidate
      break
    }
  }

  return best
}

/** `#rrggbb`, for writing a repaired colour back into the DOM. */
export function toHex([r, g, b]: Rgb): string {
  return (
    '#' +
    [r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')
  )
}
